import i18n from 'i18next';
import { put, call, takeLatest, all, takeEvery, take, delay } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { historyReplace } from '../../services/History';
import { createListActionTypes, fixEncoding } from './Constants';
import { listsActionTypes } from '../lists/Constants';
import { nameInUrlFormat } from '../../services/Helper';
import { uploadFileHelper } from '../../pagesNext/shared/Sagas';
import * as createListActions from './Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as addImageModalActions from '../../components/modals/addImage/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as sharedActions from '../../pagesNext/shared/Actions';

let isHandleCreateListRequestRunning = false;

export default function* () {
	yield all([
		takeLatest(createListActionTypes.LIST_OPTIONS_REQUEST, handleListOptionsRequest),
		takeEvery(createListActionTypes.WORD_SUGGESTION_REQUEST, handleWordSuggestionRequest),
		takeEvery(createListActionTypes.CREATE_LIST_REQUEST, handleCreateListRequest),
		takeLatest(createListActionTypes.DELETE_DRAFT_LIST_REQUEST, handleDeleteDraftListRequest),
		takeLatest(createListActionTypes.EDIT_LIST_DATA_REQUEST, handleEditListDataRequest),
		takeLatest(createListActionTypes.CLOSE_LIST_PAGE, closeCreateListPage),
		takeLatest(
			createListActionTypes.UPLOAD_WORDLIST_IMAGE_REQUEST,
			handleUploadWordlistImageRequest,
		),
	]);
}

export function* handleListOptionsRequest() {
	try {
		const { data } = yield call(processRequest, 'list_options');

		yield put(createListActions.listOptionsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(createListActions.listOptionsError());
	}
}

export function* handleWordSuggestionRequest({ payload }) {
	try {
		const { word, languageFrom, languageTo, id, column } = payload || {};
		const { data } = yield call(
			processRequest,
			`word_suggestions/search?from_lang=${languageFrom.code}&to_lang=${languageTo.code}&word=${word}`,
		);

		yield put(
			createListActions.wordSuggestionSuccess({
				id,
				column,
				suggestions: data.map(fixEncoding),
			}),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(createListActions.wordSuggestionError());
	}
}

export function* handleUploadWordlistImageRequest({ payload }) {
	try {
		const { file, callback } = payload || {};

		const { linkToFile } = yield call(uploadFileHelper, 'lists', file);

		if (callback) callback(linkToFile);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(createListActions.uploadWordlistImageError());
	}
}

export function* handleCreateListRequest({ payload }) {
	const { listData, listId, createList } = payload || {};
	try {
		if (isHandleCreateListRequestRunning && !createList) {
			while (isHandleCreateListRequestRunning) {
				yield delay(1000);
			}
		} else {
			isHandleCreateListRequestRunning = true;

			const { wordlist_image_url } = listData || {};

			const { data } = listId
				? yield call(
						processRequest,
						`lists/${listId}${wordlist_image_url ? '/update_from_image' : ''}`,
						'PATCH',
						{ list: listData },
				  )
				: yield call(
						processRequest,
						`lists${wordlist_image_url ? '/create_from_image' : ''}`,
						'POST',
						{ list: listData },
				  );

			if (createList) {
				yield put(createListActions.cleanListState());
				yield put(
					addImageModalActions.handleAddImageModal({
						imageSrc: null,
						itemId: null,
						images: [],
						type: null,
					}),
				);
				yield call(historyReplace, `/lists/${data.id}/${nameInUrlFormat(data.title)}`);
			}

			isHandleCreateListRequestRunning = false;

			if (!wordlist_image_url) {
				yield put(createListActions.createListSuccess(data, false));

				if (data.suggested_subjects)
					yield put(
						sharedActions.getUserSuggestedSubjectsSuccess(data.suggested_subjects),
					);
			}
		}
	} catch (e) {
		yield put(createListActions.createListError());
		yield put(createListActions.uploadWordlistImageError());

		if (e.response?.status === 422) {
			yield put(notificationActions.createNotification(e.response.data.error, null, true));
		} else {
			yield put(errorHandlerActions.handleError(e, 'notification'));
		}
	}
}

export function* handleDeleteDraftListRequest({ payload }) {
	try {
		const { listId } = payload || {};

		yield call(processRequest, `lists/${listId}`, 'DELETE');

		yield put(createListActions.deleteDraftListSuccess());

		yield call(historyReplace, '/');

		yield put(
			notificationActions.createNotification(
				i18n.t('create_list.success_remove_lis_notification'),
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(createListActions.deleteDraftListError());
	}
}

export function* handleEditListDataRequest({ payload }) {
	try {
		const { listId } = payload || {};

		const { data } = yield call(processRequest, `lists/${listId}/data`);

		yield put(createListActions.editListDataSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(createListActions.editListDataError(e));
	}
}

export function* closeCreateListPage() {
	try {
		yield take([
			createListActionTypes.CREATE_LIST_SUCCESS,
			listsActionTypes.GET_RECENT_LISTS_SUCCESS,
			listsActionTypes.GET_MADE_LISTS_SUCCESS,
		]);

		yield put(createListActions.cleanListState());
		yield put(
			addImageModalActions.handleAddImageModal({
				imageSrc: null,
				itemId: null,
				images: [],
				type: null,
			}),
		);
	} catch (e) {
		yield put(createListActions.createListError(e));
	}
}
